import React, { useEffect } from 'react';
import { 
  Box, 
  Container, 
  Heading, 
  Text, 
  VStack, 
  HStack, 
  Image, 
  UnorderedList, 
  ListItem,
  SimpleGrid,
  Icon,
  Flex,
  useColorModeValue,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Avatar,
  AvatarGroup,
  Badge,
  Progress
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaUsers, FaCode, FaRocket, FaAward } from 'react-icons/fa';
import { MdTimeline, MdLocationOn, MdPhone, MdEmail } from 'react-icons/md';
import config from '../../config';

const MotionBox = motion(Box);

const About = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cardBg = useColorModeValue("white", "gray.700");
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const primaryColor = useColorModeValue("blue.500", "teal.500");
  const secondaryColor = useColorModeValue("teal.500", "blue.500");

  return (
    <Box>
      <Box pt="130px" bg="gray.50">
        {/* Hero Section with Parallax Effect */}
        <Box
          backgroundImage="url(https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80)"
          backgroundSize="cover"
          backgroundPosition="center"
          backgroundAttachment="fixed"
                 top="-14"
          height="60vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          position="relative"
        >
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="rgba(0,0,0,0.6)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            color="white"
          >
            <Heading
              as="h1"
              size="2xl"
              mb={4}
              textAlign="center"
              bgGradient="linear(to-r, blue.500, teal.500)"
              bgClip="text"
              fontWeight="bold"
            >
              About {config.companyName}
            </Heading>
            <Text fontSize="xl" maxW="800px" textAlign="center" px={4} color="gray.200">
              Transforming Ideas into Digital Reality
            </Text>
          </Box>
        </Box>

        <Container maxW="container.xl" py={10}>
          {/* Company Overview Section */}
          <MotionBox
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            transition={{ duration: 0.5 }}
            mb={20}
          >
            <VStack spacing={6} alignItems="center" textAlign="center">
              <Heading
                as="h2"
                size="xl"
                bgGradient="linear(to-r, blue.500, teal.500)"
                bgClip="text"
                fontWeight="bold"
              >
                Our Story
              </Heading>
              <Text fontSize="lg" maxW="800px" color="gray.600">
                Founded in 2024, {config.companyName} is a dynamic technology company dedicated to bringing innovative digital solutions to businesses of all sizes. We believe in the power of technology to transform businesses and create meaningful impact.
              </Text>
            </VStack>
          </MotionBox>

          {/* Mission & Vision Section */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} mb={20}>
            <MotionBox
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ duration: 0.5, delay: 0.2 }}
              bg="white"
              p={8}
              borderRadius="lg"
              boxShadow="lg"
            >
              <VStack spacing={4} alignItems="flex-start">
                <Icon as={FaRocket} w={10} h={10} color={primaryColor} />
                <Heading as="h3" size="lg" color="gray.700">Our Mission</Heading>
                <Text color="gray.600">
                  To empower businesses with cutting-edge technology solutions that drive growth, efficiency, and innovation in the digital age.
                </Text>
              </VStack>
            </MotionBox>

            <MotionBox
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ duration: 0.5, delay: 0.4 }}
              bg="white"
              p={8}
              borderRadius="lg"
              boxShadow="lg"
            >
              <VStack spacing={4} alignItems="flex-start">
                <Icon as={FaAward} w={10} h={10} color={secondaryColor} />
                <Heading as="h3" size="lg" color="gray.700">Our Vision</Heading>
                <Text color="gray.600">
                  To be the leading technology partner for businesses worldwide, known for innovation, reliability, and exceptional service.
                </Text>
              </VStack>
            </MotionBox>
          </SimpleGrid>

          {/* Team Section */}
          {/* <Box mb={20}>
            <Heading
              as="h2"
              size="xl"
              bgGradient="linear(to-r, blue.500, teal.500)"
              bgClip="text"
              fontWeight="bold"
              textAlign="center"
              mb={10}
            >
              Meet Our Team
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={10}>
              {[
                {
                  name: "Kanhai Kumar",
                  role: "Co-founder & CEO",
                  image: "https://ik.imagekit.io/3ys7hawgp/Screenshot%202025-05-23%20at%208.05.52%E2%80%AFPM.png?updatedAt=1748011176575",
                  description: "Visionary leader with 10+ years of experience in technology and business development."
                },
                {
                  name: "Akash Kumar",
                  role: "Co-founder & CTO",
                  image: "https://ik.imagekit.io/3ys7hawgp/Screenshot%202025-05-23%20at%208.05.52%E2%80%AFPM.png?updatedAt=1748011176575",
                  description: "Tech innovator specializing in AI and machine learning solutions."
                },
                { 
                  name: "Harsh",
                  role: "Lead Developer",
                  image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=761&q=80",
                  description: "Full-stack developer with expertise in modern web technologies."
                }
              ].map((member, index) => (
                <MotionBox
                  key={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  bg="white"
                  p={6}
                  borderRadius="lg"
                  boxShadow="lg"
                  _hover={{ transform: 'translateY(-5px)', transition: 'all 0.3s ease' }}
                  cursor="pointer"
                  onClick={onOpen}
                >
                  <VStack spacing={4}>
                    <Avatar size="2xl" name={member.name} src={member.image} />
                    <Heading as="h3" size="md" color="gray.700">{member.name}</Heading>
                    <Badge colorScheme="teal">{member.role}</Badge>
                    <Text textAlign="center" color="gray.600">{member.description}</Text>
                  </VStack>
                </MotionBox>
              ))}
            </SimpleGrid>
          </Box> */}

          {/* Why Choose Us Section */}
          <Box mb={20}>
            <Heading
              as="h2"
              size="xl"
              bgGradient="linear(to-r, blue.500, teal.500)"
              bgClip="text"
              fontWeight="bold"
              textAlign="center"
              mb={10} 
            >
              Why Choose Us?
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
              {[
                {
                  icon: FaUsers,
                  title: "Expert Team",
                  description: "Highly skilled professionals with diverse expertise",
                  color: primaryColor
                },
                {
                  icon: FaCode,
                  title: "Innovative Solutions",
                  description: "Cutting-edge technology and modern approaches",
                  color: secondaryColor
                },
                {
                  icon: FaRocket,
                  title: "Rapid Development",
                  description: "Accelerated project delivery with agile methodologies",
                  color: primaryColor
                },
                {
                  icon: FaAward,
                  title: "Client Satisfaction",
                  description: "Dedicated to exceeding client expectations and building lasting partnerships",
                  color: secondaryColor
                }
              ].map((item, index) => (
                <MotionBox
                  key={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  bg={cardBg}
                  p={6}
                  borderRadius="lg"
                  boxShadow="md"
                  _hover={{ boxShadow: "xl", transform: "translateY(-5px)" }}
                >
                  <VStack spacing={4} align="center">
                    <Icon as={item.icon} w={12} h={12} color={item.color} />
                    <Heading as="h3" size="md" textAlign="center" color="gray.700">
                      {item.title}
                    </Heading>
                    <Text fontSize="sm" textAlign="center" color="gray.600">
                      {item.description}
                    </Text>
                  </VStack>
                </MotionBox>
              ))}
            </SimpleGrid>
          </Box>

          {/* Statistics Section */}
          <Box mb={20} bg="white" p={10} borderRadius="lg" boxShadow="lg">
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
              {[
                { label: "Projects Completed", value: "100+" },
                { label: "Happy Clients", value: "50+" },
                { label: "Team Members", value: "20+" },
                { label: "Years Experience", value: "5+" }
              ].map((stat, index) => (
                <Stat key={index} textAlign="center">
                  <StatNumber fontSize="3xl" color="teal.500">{stat.value}</StatNumber>
                  <StatLabel color="gray.600">{stat.label}</StatLabel>
                </Stat>
              ))}
            </SimpleGrid>
          </Box>

          {/* Call to Action Section */}
          <MotionBox
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            transition={{ duration: 0.5 }}
            bgGradient="linear(to-r, blue.500, teal.500)"
            p={10}
            borderRadius="lg"
            textAlign="center"
            color="white"
          >
            <VStack spacing={6}>
              <Heading as="h2" size="xl">
                Ready to Start Your Project?
              </Heading>
              <Text fontSize="lg" maxW="600px">
                Let's work together to bring your ideas to life. Contact us today for a free consultation.
              </Text>
              <Button
                size="lg"
                bg="white"
                color="teal.500"
                _hover={{ bg: "gray.100" }}
                onClick={() => window.location.href = '/contact'}
              >
                Get Started
              </Button>
            </VStack>
          </MotionBox>

          {/* Quick Contact */}
          <Box w="100%">
            <Heading 
              size="md" 
              mb={4}
              bgGradient="linear(to-r, blue.400, purple.500)"
              bgClip='text'
              fontWeight="bold"
            >
              Quick Contact
            </Heading>
            <VStack align="start" spacing={3}>
              <HStack spacing={3}>
                <Icon as={MdPhone} color={primaryColor} />
                <Text>+91 9473421755</Text>
              </HStack>
              <HStack spacing={3}>
                <Icon as={MdEmail} color={primaryColor} />
                <Text>partners@orivanta.ai</Text>
              </HStack>
            </VStack>
          </Box>
        </Container>
      </Box>

      {/* Team Member Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent bg="white">
          <ModalHeader color="gray.700">Team Member Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <Text color="gray.600">
                Click on any team member card to view their detailed profile, including:
              </Text>
              <UnorderedList color="gray.600">
                <ListItem>Professional background</ListItem>
                <ListItem>Areas of expertise</ListItem>
                <ListItem>Notable achievements</ListItem>
                <ListItem>Contact information</ListItem>
              </UnorderedList>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default About;